import { useNavigate, useParams } from "react-router-dom";
import {
  answerDisplay,
  answerIsSelected,
  stripUnnecessaryMarkup,
  updateAnswerSelections,
  useAssessmentEvent,
  useCompleteAssessmentRequest,
  useSaveAssessmentProgress
} from "../assessmentEventUtils";
import {
  Alert,
  Button,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography
} from "@mui/material";
import DataSaverOffIcon from "@mui/icons-material/DataSaverOff";
import ChatIcon from "@mui/icons-material/Chat";
import BackIcon from "@mui/icons-material/ArrowBackIos";
import NextIcon from "@mui/icons-material/ArrowForwardIos";
import Grid from "@mui/material/Unstable_Grid2";
import { APIError } from "../../../common-ui/APIError";
import { PageWrapper, PageWrapperProps } from "../../../common-ui/PageWrapper";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AssessmentCard } from "../../Assessments/AssessmentCard";
import { useAppSelector } from "../../../app/hooks";
import { selectCurrentUser } from "../../Users/usersSlice";
import { AnswerOptionCard } from "../../../common-ui/AnswerOptionCard";
import { LoadingButton } from "@mui/lab";
import { OptionCard } from "../../../common-ui/OptionCard";
import { useDispatch } from "react-redux";
import {
  setShowToast,
  setToastMessage,
  setToastStyle
} from "../../../common-ui/toastSlice";
import { completeAssessmentNotifications } from "../../../app/constants";
import { useBlockerWithConfirmation } from "../../../common-ui/ConfirmNav";
import { Mixpanel } from "../../Analytics/Mixpanel";
import { MixpanelEvents } from "../../../app/constants";
import {
  selectFeatureFlags,
  selectFeatureFlagsError,
  selectFeatureFlagsStatus
} from "../../Firebase/firebaseSlice";
import {
  QuestionAnswerOption,
  InputType,
  AnswerOption
} from "../assessmentEventsAPI";
import { answersToSubmittedPayload } from "../submittedAnswersFromOptions";

type CompleteAssessmentStep = QuestionAnswerOption | string;

const returnSortedAnswerOptions = (step: CompleteAssessmentStep) => {
  if (typeof step === "string") return;
  const answerOptions = step.answerOptions;
  return answerOptions;
};

const stepIsRequired = (step: CompleteAssessmentStep) => {
  if (typeof step === "string") return false;
  const inputType = step.inputTypeName;
  if (
    inputType === InputType.Radio ||
    inputType === InputType.Checkbox ||
    (inputType === InputType.Feedback && step.required)
  )
    return true;
  return false;
};

const pageDetails: PageWrapperProps = {
  className: "CompleteAssessmentEvent",
  headerText: "Complete Assessment Event"
};

const CHOICE_AUTOSAVE_DEBOUNCE_MS = 350;

export const CompleteAssessmentEvent = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const { assessmentEventId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    assessmentDetails,
    status: assessmentEventStatus,
    error: assessmentEventError
  } = useAssessmentEvent(assessmentEventId);

  const flags = useAppSelector(selectFeatureFlags);
  const firebaseStatus = useAppSelector(selectFeatureFlagsStatus);
  const firebaseError = useAppSelector(selectFeatureFlagsError);

  const {
    status: completeAssessmentRequestStatus,
    error: completeAssessmentRequestError,
    completeAssessmentRequest
  } = useCompleteAssessmentRequest();
  const {
    status: saveProgressStatus,
    saveResult,
    saveAssessmentProgress
  } = useSaveAssessmentProgress();
  const { activity_notes_visible } =
    flags;

  const sortedQuestions = assessmentDetails?.questions ?? [];
  const steps: CompleteAssessmentStep[] = [...(sortedQuestions ?? [])];
  steps.push("Summary");

  const necessaryDataStatuses = [assessmentEventStatus, firebaseStatus];
  const necessaryDataErrors = [assessmentEventError, firebaseError];

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [activeStepPrompt, setActiveStepPrompt] = useState<string>("");
  const [answerProvidedForActiveStep, setAnswerProvidedForActiveStep] =
    useState(false);
  const activeStep = steps[activeStepIndex];
  const activeStepIsQuestion = typeof activeStep !== "string";
  const activeStepQuestion = sortedQuestions[activeStepIndex];
  const onFinalStep = activeStepIndex === steps.length - 1;

  const [answers, setAnswers] = useState<AnswerOption[]>([]);
  const [hydratedFromServer, setHydratedFromServer] = useState(false);
  const progressSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveQuestionIdRef = useRef<string | null>(null);
  const pendingSaveInputTypeRef = useRef<InputType | null>(null);
  const answersForCurrentQuestion = answers.filter(
    (answer) =>
      activeStepIsQuestion && answer.questionId === activeStepQuestion.id
  );

  const progressSavedText = useMemo(() => {
    if (saveProgressStatus === "loading") {
      return "Saving...";
    }
    if (saveProgressStatus === "succeeded" && saveResult?.lastSavedAt) {
      const savedAt = new Date(saveResult.lastSavedAt);
      if (!Number.isNaN(savedAt.getTime())) {
        return `Saved at ${savedAt.toLocaleString()}`;
      }
      return "Saved";
    }
    if (
      saveProgressStatus === "idle" &&
      assessmentDetails?.lastProgressSavedAt
    ) {
      const savedAt = new Date(assessmentDetails.lastProgressSavedAt);
      if (!Number.isNaN(savedAt.getTime())) {
        return `Saved at ${savedAt.toLocaleString()}`;
      }
    }
    if (saveProgressStatus === "failed") {
      return "Save failed. Changes may be unsaved.";
    }
    return "";
  }, [
    saveProgressStatus,
    saveResult?.lastSavedAt,
    assessmentDetails?.lastProgressSavedAt
  ]);

  const saveProgress = async (
    submittedAnswers: ReturnType<typeof answersToSubmittedPayload>
  ): Promise<boolean> => {
    if (!assessmentDetails?.assessmentEventId || submittedAnswers.length === 0) {
      return true;
    }
    const result = await saveAssessmentProgress(
      assessmentDetails.assessmentEventId,
      submittedAnswers
    );
    return result !== null;
  };

  const submittedAnswersForQuestion = (
    nextAnswers: AnswerOption[],
    questionId: string,
    inputTypeName?: InputType
  ) => {
    const questionAnswers = nextAnswers.filter((answer) => answer.questionId === questionId);
    // Empty selection means "clear this question." Backend treats a single ""
    // entry as a clear for any input type (Checkbox via all-empty-array,
    // others via trailing-empty), so we emit it uniformly here.
    if (questionAnswers.length === 0) {
      return [{ questionId, value: "" }];
    }
    return answersToSubmittedPayload(questionAnswers);
  };

  const scheduleProgressSave = (
    nextAnswers: AnswerOption[],
    questionId: string,
    inputTypeName: InputType,
    debounceMs: number
  ) => {
    if (!hydratedFromServer) {
      return;
    }
    if (progressSaveTimerRef.current) {
      clearTimeout(progressSaveTimerRef.current);
    }
    pendingSaveQuestionIdRef.current = questionId;
    pendingSaveInputTypeRef.current = inputTypeName;
    progressSaveTimerRef.current = setTimeout(() => {
      const submittedAnswers = submittedAnswersForQuestion(
        nextAnswers,
        questionId,
        inputTypeName
      );
      void saveProgress(submittedAnswers);
      pendingSaveQuestionIdRef.current = null;
      pendingSaveInputTypeRef.current = null;
      progressSaveTimerRef.current = null;
    }, debounceMs);
  };

  const shouldBlock =
    answers.length > 0 &&
    answers.some((answer) => answer) &&
    ![completeAssessmentRequestStatus].includes("succeeded");

  useBlockerWithConfirmation({
    title: completeAssessmentNotifications.NavTitle,
    prompt: completeAssessmentNotifications.NavPrompt,
    confirmText: completeAssessmentNotifications.NavConfirm,
    cancelText: completeAssessmentNotifications.NavCancel,
    shouldBlock: shouldBlock
  });

  const activeStepAnswerOptions = returnSortedAnswerOptions(activeStep);

  const activeStepRequired = stepIsRequired(activeStep);

  // SE-1852: network-configured minimum word count for narrative Feedback answers.
  // 0 = no minimum. Empty answers are not gated here (that's the required check);
  // a non-empty Feedback answer under the minimum blocks advancing/finishing.
  const feedbackMinWords = assessmentDetails?.feedbackMinWords ?? 0;
  const countWords = (s: string) =>
    (s ?? "").trim().split(/\s+/).filter(Boolean).length;
  const feedbackBelowMin = (value: string) =>
    feedbackMinWords > 0 &&
    value.trim() !== "" &&
    countWords(value) < feedbackMinWords;

  const activeFeedbackValue =
    activeStepIsQuestion &&
    activeStep.inputTypeName === InputType.Feedback
      ? answersForCurrentQuestion[0]?.value || ""
      : "";
  const activeStepFeedbackBelowMin = feedbackBelowMin(activeFeedbackValue);
  const anyFeedbackBelowMin =
    feedbackMinWords > 0 &&
    sortedQuestions.some(
      (q) =>
        q.inputTypeName === InputType.Feedback &&
        feedbackBelowMin(
          answers.find((a) => a.questionId === q.id)?.value || ""
        )
    );

  const handleBack = () => {
    setActiveStepIndex(activeStepIndex - 1);
  };

  const handleNext = async () => {
    if (onFinalStep) {
      if (assessmentDetails) {
        if (progressSaveTimerRef.current) {
          clearTimeout(progressSaveTimerRef.current);
          progressSaveTimerRef.current = null;
        }
        const pendingQuestionId = pendingSaveQuestionIdRef.current;
        const pendingInputType = pendingSaveInputTypeRef.current;
        const flushed = await saveProgress(
          pendingQuestionId
            ? submittedAnswersForQuestion(
                answers,
                pendingQuestionId,
                pendingInputType ?? undefined
              )
            : []
        );
        pendingSaveQuestionIdRef.current = null;
        pendingSaveInputTypeRef.current = null;
        if (!flushed) {
          dispatch(setToastMessage(t("Could not save your latest answers. Please try again.")));
          dispatch(setToastStyle("error"));
          dispatch(setShowToast(true));
          return;
        }
        await completeAssessmentRequest(assessmentDetails.assessmentEventId, []);
      }
    } else {
      setActiveStepIndex(activeStepIndex + 1);
    }
  };

  useEffect(() => {
    if (activeStepIsQuestion) {
      setActiveStepPrompt(activeStep.prompt ?? "N/A");
      const answerForCurrentQuestion = !!answers.find(
        (answer) => answer.questionId === activeStepQuestion?.id
      );
      setAnswerProvidedForActiveStep(answerForCurrentQuestion);
    }
  }, [activeStep, activeStepIsQuestion, answers]);

  useEffect(() => {
    if (!assessmentDetails || hydratedFromServer) {
      return;
    }
    const restoredAnswers: AnswerOption[] = [];
    for (const question of assessmentDetails.questions ?? []) {
      const savedValue = question.savedAnswer?.value;
      if (!savedValue) {
        continue;
      }
      if (question.inputTypeName === InputType.Checkbox) {
        try {
          const parsedValues = JSON.parse(savedValue) as string[];
          if (Array.isArray(parsedValues)) {
            for (const parsedValue of parsedValues) {
              const matchingOption = question.answerOptions.find(
                (option) => option.value === parsedValue
              );
              if (matchingOption) {
                restoredAnswers.push(matchingOption);
              }
            }
          }
        } catch (_error) {
          // Ignore malformed historical values and continue rendering.
        }
        continue;
      }
      if (question.inputTypeName === InputType.Radio) {
        const matchingOption = question.answerOptions.find(
          (option) => option.value === savedValue
        );
        if (matchingOption) {
          restoredAnswers.push(matchingOption);
        }
        continue;
      }
      if (question.inputTypeName === InputType.Feedback) {
        restoredAnswers.push({
          id: `saved-${question.id}`,
          questionId: question.id,
          value: savedValue,
          createdAt: question.savedAnswer?.updatedAt || new Date().toISOString(),
          updatedAt: question.savedAnswer?.updatedAt || new Date().toISOString()
        });
      }
    }
    setAnswers(restoredAnswers);
    const answeredQuestionIds = new Set(restoredAnswers.map((answer) => answer.questionId));
    const lastAnsweredQuestionIndex = (assessmentDetails.questions ?? []).reduce(
      (latestAnsweredIndex, question, questionIndex) =>
        answeredQuestionIds.has(question.id) ? questionIndex : latestAnsweredIndex,
      -1
    );
    if (lastAnsweredQuestionIndex >= 0) {
      setActiveStepIndex(lastAnsweredQuestionIndex);
    }
    setHydratedFromServer(true);
  }, [assessmentDetails, hydratedFromServer]);

  useEffect(() => {
    return () => {
      if (progressSaveTimerRef.current) {
        clearTimeout(progressSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if ([completeAssessmentRequestStatus].includes("succeeded")) {
      dispatch(setToastMessage("Assessment has been successfully completed."));
      dispatch(setToastStyle("success"));
      dispatch(setShowToast(true));
      Mixpanel.track(MixpanelEvents.AssessmentRatingCompleted);
      if (!shouldBlock) {
        navigate("/assessments/pending");
      }
    }
    if ([completeAssessmentRequestStatus].includes("failed")) {
      dispatch(
        setToastMessage(
          `There has been an error completing the assessment: ${
            completeAssessmentRequestError ||
            "Unknown Error"
          }`
        )
      );
      dispatch(setToastStyle("error"));
      dispatch(setShowToast(true));
    }
  }, [completeAssessmentRequestStatus, completeAssessmentRequestError, shouldBlock]);

  useEffect(() => {
    Mixpanel.track(MixpanelEvents.AssessmentRatingStarted);
  }, []);

  if (necessaryDataStatuses.includes("loading")) return <CircularProgress />;

  if (necessaryDataStatuses.includes("failed")) {
    const firstError = necessaryDataErrors[0];
    return <APIError error={firstError || "API Error"} />;
  }

  if (
    necessaryDataStatuses.every((status) => status === "succeeded") &&
    assessmentDetails
  ) {
    if (assessmentDetails.assessmentEventDeclined) {
      return (
        <Alert severity="info">{t("This assessment has been declined.")}</Alert>
      );
    }
    if (assessmentDetails.assessmentEventCompletionDate) {
      return (
        <Alert severity="info">
          {t("This assessment has already been completed.")}
        </Alert>
      );
    }
    return (
      <PageWrapper {...pageDetails}>
        <Grid
          container
          xs={12}
          mdOffset={2}
          md={8}
          p={2}
          spacing={2}
          sx={{ display: "flex", flex: 1, minHeight: 0 }}
        >
          <Grid xs={12}>
            <Stepper activeStep={activeStepIndex}>
              {steps.map((step) => {
                const stepProps: { completed?: boolean } = {};
                const labelProps: { optional?: ReactNode } = {};
                const isQuestion = typeof step !== "string";
                return (
                  <Step
                    key={isQuestion ? step.id : step}
                    {...stepProps}
                    sx={{
                      "& .MuiStepLabel-root .Mui-completed": {
                        color: "primary.main"
                      },
                      "& .MuiStepLabel-root .Mui-active": {
                        color: "secondary.main"
                      }
                    }}
                  >
                    <StepLabel {...labelProps}>
                      {t(isQuestion ? step.title ?? "" : step)}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Grid>
          <Grid xs={12} md={10} mdOffset={1} spacing={2}>
            <Grid xs={12}>
              <AssessmentCard
                assessmentDetails={assessmentDetails}
                onClick={() => null}
                selfAssessment={
                  assessmentDetails?.assessmentEventSubjectUserId === currentUser?.id
                }
              />
            </Grid>
            {activity_notes_visible && assessmentDetails.activityEventNotes && (
              <Grid xs={12}>
                <Typography variant="subtitle2" color="text.primary">
                  {t(
                    "Note from " +
                      (assessmentDetails?.activityEventNotesAuthorName ??
                        assessmentDetails?.user.fullName) +
                      " about this encounter"
                  )}
                  :{" "}
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {assessmentDetails.activityEventNotes}
                    </Typography>
                  </Typography>
                </Grid>
              )}
            {activeStepIsQuestion && (
              <>
                {progressSavedText && (
                  <Grid xs={12} display="flex" justifyContent="center">
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                      color={saveProgressStatus === "failed" ? "error" : "text.secondary"}
                    >
                      {progressSavedText}
                    </Typography>
                  </Grid>
                )}
                {/* Prompt */}
                <Grid xs={12} display="flex" justifyContent="center">
                  <Typography variant="h6" sx={{ whiteSpace: "pre-line" }}>
                    {t(activeStepPrompt)}
                  </Typography>
                </Grid>

                {/* Help Text */}
                {activeStep.helpText && (
                  <Grid xs={12} display="flex" justifyContent="flex-start">
                    <Typography
                      color={"#00000099"}
                      dangerouslySetInnerHTML={{
                        __html: stripUnnecessaryMarkup(activeStep.helpText)
                      }}
                    />
                  </Grid>
                )}
              </>
            )}
            {activeStepIsQuestion && (
              <>
                {activeStep.inputTypeName &&
                  [InputType.Radio, InputType.Checkbox].includes(
                    activeStep.inputTypeName
                  ) &&
                  activeStepAnswerOptions &&
                  activeStepAnswerOptions.map((answerOption) => {
                    if (answerOption) {
                      const onClick = () => {
                        const updatedAnswers = updateAnswerSelections({
                          question: activeStepQuestion,
                          currentAnswers: answers,
                          setAnswers,
                          answerOption
                        });
                        scheduleProgressSave(
                          updatedAnswers,
                          activeStepQuestion.id,
                          activeStep.inputTypeName,
                          CHOICE_AUTOSAVE_DEBOUNCE_MS
                        );
                      };
                      return (
                        <Grid xs={12} key={answerOption.id}>
                          <AnswerOptionCard
                            inputType={activeStep.inputTypeName ?? InputType.Radio}
                            answerOption={answerOption}
                            selected={answerIsSelected(answerOption, answers)}
                            onClick={onClick}
                          />
                        </Grid>
                      );
                    }
                  })}
                {activeStep.inputTypeName === InputType.Feedback && (
                  <Grid xs={12}>
                    <TextField
                      key={activeStepIndex}
                      label={activeStep.title}
                      value={answersForCurrentQuestion[0]?.value || ""}
                      onChange={(event) => {
                        const updatedAnswers = updateAnswerSelections({
                          question: activeStepQuestion,
                          currentAnswers: answers,
                          setAnswers,
                          stringValue: event.target.value
                        });
                        scheduleProgressSave(
                          updatedAnswers,
                          activeStepQuestion.id,
                          activeStep.inputTypeName,
                          700
                        );
                      }}
                      multiline
                      rows={4}
                      fullWidth
                      error={activeStepFeedbackBelowMin}
                      helperText={
                        feedbackMinWords > 0
                          ? t(
                              "A minimum of {{min}} words is required ({{count}} entered).",
                              {
                                min: feedbackMinWords,
                                count: countWords(activeFeedbackValue)
                              }
                            )
                          : undefined
                      }
                    />
                  </Grid>
                )}
              </>
            )}
            {onFinalStep && (
              <>
                {sortedQuestions.map((question) => {
                  if (question.inputTypeName === InputType.NoInput) return;
                  const selectedAnswers = answers.filter(
                    (answer) => answer.questionId === question.id
                  );
                  const sortedSelectedAnswers = selectedAnswers.sort(
                    (a, b) => (a.sortingIndex || 0) - (b.sortingIndex || 0)
                  );
                  const answerToDisplay = answerDisplay(
                    question,
                    sortedSelectedAnswers
                  );
                  const avatar =
                    question.inputTypeName === InputType.Feedback ? (
                      <ChatIcon />
                    ) : (
                      <DataSaverOffIcon />
                    );
                  return (
                    <Grid xs={12} key={question.id}>
                      <OptionCard
                        avatarContent={avatar}
                        title={question.title || "N/A"}
                        subtitle={answerToDisplay}
                        filledBackground
                        bordered
                      />
                    </Grid>
                  );
                })}
              </>
            )}
            <Grid xs={12} display="flex" justifyContent="center">
              <Grid>
                <Button
                  onClick={handleBack}
                  disabled={
                    activeStepIndex === 0 ||
                    [completeAssessmentRequestStatus].includes("loading")
                  }
                  startIcon={<BackIcon />}
                  variant="outlined"
                >
                  {t("BACK")}
                </Button>
              </Grid>
              <Grid>
                <LoadingButton
                  onClick={handleNext}
                  endIcon={<NextIcon />}
                  variant={onFinalStep ? "contained" : "outlined"}
                  loading={[completeAssessmentRequestStatus].includes(
                    "loading"
                  )}
                  disabled={
                    (activeStepRequired && !answerProvidedForActiveStep) ||
                    activeStepFeedbackBelowMin ||
                    (onFinalStep && anyFeedbackBelowMin) ||
                    [completeAssessmentRequestStatus].includes("loading")
                  }
                >
                  {t(onFinalStep ? "SAVE & FINISH" : "NEXT")}
                </LoadingButton>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </PageWrapper>
    );
  }

  return null;
};
